// src/belediye/BelediyeApp.js

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Route,
  Routes,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
  Navigate,
} from "react-router-dom";

import ApplicationDetailsPage from "./pages/ApplicationDetailsPage";

import NewApplicationsPage from "./pages/NewApplicationsPage";
import PendingApplicationsPage from "./pages/PendingApplicationsPage";
import RedirectedApplicationsPage from "./pages/RedirectedApplicationsPage";
import DashboardV2Page from "./pages/dashboard/DashboardV2Page";
import ReportsPage from "./pages/reports/ReportsPage";
import ResolvedApplicationsPage from "./pages/ResolvedApplicationsPage";
import UnresolvedApplicationsPage from "./pages/UnresolvedApplicationsPage";

import SahaPersoneliAssignmentDetailPage from "./pages/saha/SahaPersoneliAssignmentDetailPage";
import SahaPersoneliDashboardPage from "./pages/saha/SahaPersoneliDashboardPage";
import NotificationsPage from "./pages/notifications/NotificationsPage";
// FAZ-84 — telefon bildirimi köprü rotası (/yonetim/b/:bildirimId)
import BildirimKopruPage from "./pages/notifications/BildirimKopruPage";
import PushBaslatici from "./components/notifications/PushBaslatici";
import DuyuruOlusturPage from "./pages/bildirimler/DuyuruOlusturPage";
import ProfilePage from "./pages/profile/ProfilePage";

import UnifiedLoading from "./components/Loading/UnifiedLoading";
// Yükleme/splash ekranı — eski yuvarlak KİM logosu (kullanıcı isteği)
import kimLogo from "./assets/images/kim_logo.png";
import SurveyReportDetailPage from "./pages/survey/SurveyReportDetailPage";
import SurveyReportsPage from "./pages/survey/SurveyReportsPage";

import Layout from "./components/BelediyeLayout";
import BelediyeLayoutNoSidebar from "./components/BelediyeLayoutNoSidebar";
import FloatingBackButton from "./components/FloatingBackButton";
// FAZ-90: alt navigasyon barı (yalnız telefon — katman kontrolü kendi içinde)
import AltBar from "./components/AltBar";
// FAZ-90: alt bardaki "Çıkış yap" TEK çıkış kaynağını kullanır
import { performLogout } from "./components/BelediyeLogoutButton";

import UnitSelectionPage from "./pages/UnitSelectionPage";

import api, { BELEDIYE_GLOBAL_LOADING_EVENT } from "./services/belediyeApi";
// FAZ-63 — derin link hedefini giriş sayfasına taşıyan yardımcı (açık yönlendirme süzgeçli).
import { loginUrlWithNext } from "../vatandas/utils/safeNext";

//   Halk Masası pages (planlanan isimler)

import HalkMasasiLookupPage from "./pages/halkMasasi/HalkMasasiLookupPage";
import HalkMasasiCitizenProfilePage from "./pages/halkMasasi/HalkMasasiCitizenProfilePage";
import HalkMasasiCreateApplicationPage from "./pages/halkMasasi/HalkMasasiCreateApplicationPage";
import HalkMasasiApplicationDetailPage from "./pages/halkMasasi/HalkMasasiApplicationDetailPage";

//   redux hooks + feature flags thunk/selectors
import { useDispatch, useSelector } from "react-redux";
import {
  fetchFeatureFlags,
  selectHalkMasasiEnabled,
  selectIsFeatureFlagsLoaded,
  selectIsFeatureFlagsLoading,
} from "./redux/slices/belediyeFeatureFlagsSlice";

//   SessionRole helpers (tek kaynak)
import { getSessionRole, isDeskStaff } from "./utils/sessionRole";

//   FAZ-12: authz altyapısı (permission_ui şalteri kapalıyken etkisiz)
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { authzSetFromMe, authzCleared, selectAuthz } from "./redux/slices/authzSlice";
import YetkisizPage from "./pages/YetkisizPage";

//   FAZ-15: Sistem Yönetimi workspace sayfaları
import SistemKullanicilarPage from "./pages/sistem/SistemKullanicilarPage";
import SistemKullaniciDetayPage from "./pages/sistem/SistemKullaniciDetayPage";
import SistemRollerPage from "./pages/sistem/SistemRollerPage";
// NOT: SistemRolDetayPage artık route'lanmıyor (tek sayfa master-detail). Dosya
// yalnız mevcut birim testi (sistem.rol_kapsam.test.js) için korunur.
import SistemBirimAgaciPage from "./pages/sistem/SistemBirimAgaciPage";
//   FAZ-24: yeni yönetim ekranları (Sistem Yönetimi grubu)
import SistemMahallelerPage from "./pages/sistem/SistemMahallelerPage";
import SistemIdariSinirlarPage from "./pages/sistem/SistemIdariSinirlarPage";
import SistemVatandaslarPage from "./pages/sistem/SistemVatandaslarPage";
import SistemVatandasDetayPage from "./pages/sistem/SistemVatandasDetayPage";
import PersonelDizinPage from "./pages/personel/PersonelDizinPage";
import PersonelProfilPage from "./pages/personel/PersonelProfilPage";

//   FAZ-13: route guard + landing cutover'ı (yalnız uiOn=true dalında etkin)
import usePermissions from "./hooks/usePermissions";
import PermissionGate from "./components/authz/PermissionGate";
// FAZ-52: modül kapalıysa rota da kapanır (sidebar gizlemek yetmiyordu)
import ModuleGate from "./components/authz/ModuleGate";
import { resolveLanding } from "./nav";
import { resolveActiveUnit } from "./utils/activeUnit";
// FAZ-80: /me imzası — aynı yanıt tekrar gelince dispatch edilmesin (bkz. dosya).
import { meImzasi } from "./utils/meImzasi";

//   FAZ-28: Kurum İçi Görevler sayfaları
//   FAZ-56 (2026-07-28): tek sayfa İKİYE ayrıldı — Görev Ata (veren) /
//   Görevlerim (yapan) + Görev Raporu. Kapsam sekmeden değil SAYFADAN gelir.
import GorevAtaPage from "./pages/gorevler/GorevAtaPage";
import GorevlerimPage from "./pages/gorevler/GorevlerimPage";
// FAZ-91: Talepler kapısı — hangi sayfanın çizileceğini taraf seçimi belirler
import TaleplerPage from "./pages/gorevler/TaleplerPage";
import GorevDetayPage from "./pages/gorevler/GorevDetayPage";
// FAZ-94 — mesajlaşma telefonda gerçek rota
import SohbetlerPage from "./pages/sohbet/SohbetlerPage";
import SohbetDetayPage from "./pages/sohbet/SohbetDetayPage";
import YeniSohbetPage from "./pages/sohbet/YeniSohbetPage";
import ChatDock from "./components/mesajlasma/ChatDock";

const BASE = "/yonetim";

const LS = {
  accessToken: "accessToken",
  refreshToken: "refreshToken",
  user: "user",

  // multi-unit seçimi
  activeUnitSlug: "activeUnitSlug",
  activeUnitId: "activeUnitId",
  activeEffectiveRole: "activeEffectiveRole",
};

function safeJsonParse(value) {
  try {
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
}

function normalizeUnitsFromMe(me, meUnits, unitDetailFallback) {
  if (Array.isArray(meUnits) && meUnits.length > 0) return meUnits;

  if (me && typeof me.unit === "number" && unitDetailFallback?.slug) {
    return [
      {
        unit_id: me.unit,
        unit_name: unitDetailFallback.name || "",
        unit_slug: unitDetailFallback.slug || "",
        is_manager: false,
        is_active: true,
        role: me.role || "",
        effective_role: me.role || "",
      },
    ];
  }

  return [];
}

function findUnitBySlug(units, unitSlug) {
  return (Array.isArray(units) ? units : []).find((u) => u.unit_slug === unitSlug);
}

function computeSessionRole({ user, units, unitSlug }) {
  if (!user) return "";
  if (user.role === "başkan") return "başkan";
  const u = unitSlug ? findUnitBySlug(units, unitSlug) : null;
  return u?.effective_role || u?.role || user.role || "";
}

function pickDefaultUnitSlug({ user, units }) {
  if (!user) return "";
  if (user.role === "başkan") return "baskanlik";

  const list = Array.isArray(units) ? units : [];
  if (list.length === 1) return list[0].unit_slug;

  return "";
}

function readActiveSlugOrDefault({ user, units }) {
  return localStorage.getItem(LS.activeUnitSlug) || pickDefaultUnitSlug({ user, units }) || "";
}

function makeDashboardPath({ user, unitSlug }) {
  if (!user) return "/giris";
  if (user.role === "başkan") return `${BASE}/baskanlik/dashboard`;

  //   Halk Masası global rota
  if (user.role === "personel_2") return `${BASE}/halk-masasi`;

  if (!unitSlug) return `${BASE}/birim-sec`;
  return `${BASE}/${unitSlug}/dashboard`;
}

/* FAZ-56 — eski /yonetim/gorevler yer imi.
   Tek "Görevler" sayfası ikiye ayrıldı; hangi sayfaya düşeceği İZİNDEN türer:
   veren yetkisi olan Görev Ata'ya, yalnız iş yapan Görevlerim'e gider. Sabit
   yönlendirme yapılsaydı personel yetkisiz sayfaya düşüp "yetkiniz yok" görürdü. */
function GorevlerEskiYerImi() {
  const { canAny } = usePermissions();
  const verenMi = canAny(["tasks.view_created", "tasks.view_all"]);
  return <Navigate to={verenMi ? "/yonetim/gorev-ata" : "/yonetim/gorevlerim"} replace />;
}

// ── FAZ-80 — ROUTE KABUKLARI MODÜL KAPSAMINDA ──────────────────────────────
// KUSUR (2026-07-30, kullanıcı: "fotoğrafı seçince tak sayfa yenileniyor").
// Bu altı kabuk BelediyeApp'in GÖVDESİNDE tanımlıydı. Bir bileşen her çizimde
// YENİDEN tanımlanınca React onu YENİ BİR BİLEŞEN TÜRÜ sayar: aynı konumdaki
// eski ağacı söker, yerine sıfırdan kurar. Yani BelediyeApp'in HER yeniden
// çizimi o an açık olan sayfayı ve altındaki tüm state'i (sohbette gönderim
// bekleyen fotoğraf, sihirbazın adımları, form girdileri) siliyordu.
//
// Tetikleyici zincir: yerel dosya penceresi açılınca pencere odağı gider;
// dosya seçilip pencere kapanınca `focus` ateşlenir → aşağıdaki /me tazelemesi
// authz slice'ını besler → BelediyeApp çizilir → kabuk kimliği değişir → sayfa
// söküldü. Kullanıcı bunu "sayfa yenilendi, fotoğraf gitti" olarak görüyordu.
//
// Kabuklar artık modül kapsamında ve KİMLİKLERİ SABİT: BelediyeApp yeniden
// çizilse de React aynı bileşeni görür, alt ağaç YERİNDE kalır. Kabukların
// ihtiyaç duyduğu canlı değerlerden authz/bayraklar kendi hook'larıyla, sayfa
// bağlamı (user/units/fallback) KabukContext ile gelir.
//
// UYARI: Bu dosyaya bir daha ASLA gövde-içi bileşen tanımı eklenmeyecek —
// nöbetçisi __tests__/kabuk.remount.test.js.
const KabukContext = createContext({ user: null, units: [], effectiveFallbackPath: "" });
const useKabuk = () => useContext(KabukContext);

const municipalityName =
  process.env.REACT_APP_MUNICIPALITY_NAME ||
  process.env.REACT_APP_SYSTEM_DISPLAY_NAME ||
  "Arsuz Belediyesi";

const renderFullscreenLoading = (title = "Yükleniyor", subtitle = "Lütfen bekleyiniz...") => (
  <UnifiedLoading
    visible={true}
    variant="fullscreen"
    municipalityName={municipalityName}
    logoSrc={kimLogo}
    title={title}
    subtitle={subtitle}
  />
);

const WithSidebarLayoutRoute = () => {
  const { user, units } = useKabuk();
  const { uiOn } = usePermissions();
  const authz = useSelector(selectAuthz);
  const { unitSlug } = useParams();

  // Halk masası kullanıcılarını buradan uzak tut
  // FAZ-13 (1.4): uiOn'da bu KİLİT kalkar — erişim izin meselesidir
  // (sayfa Gate'leri korur); karma izinli kullanıcı birim kabuğuna girebilir.
  if (!uiOn && user.role === "personel_2") {
    return <Navigate to={`${BASE}/halk-masasi`} replace />;
  }

  // FAZ-13 (2): başkanlık kabuğu — uiOn'da rol string'ine EK olarak
  // scope.mode==="all_units"'e de bakar. role_ref='baskan' atanmış ama legacy
  // role'ü boş kullanıcı da bu kabuğa girer; yoksa landing baskanlik/dashboard'a
  // gönderip kabuk tanımayınca geri sekerdi. uiOn=false dalı BİREBİR (yalnız string).
  const isBaskanlikShell =
    user.role === "başkan" || (uiOn && authz.scope?.mode === "all_units");
  if (isBaskanlikShell) {
    if (unitSlug !== "baskanlik") return <Navigate to={`${BASE}/baskanlik/dashboard`} replace />;
    return (
      <Layout user={user} unitName="Başkanlık" unitSlug="baskanlik">
        <Outlet />
      </Layout>
    );
  }

  const unit = findUnitBySlug(units, unitSlug);
  if (!unit) {
    const active = localStorage.getItem(LS.activeUnitSlug);
    if (active) return <Navigate to={`${BASE}/${active}/dashboard`} replace />;
    return <Navigate to={`${BASE}/birim-sec`} replace />;
  }

  localStorage.setItem(LS.activeUnitSlug, unit.unit_slug);
  localStorage.setItem(LS.activeUnitId, String(unit.unit_id));
  localStorage.setItem(
    LS.activeEffectiveRole,
    unit.effective_role || unit.role || user.role || ""
  );

  const sessionRole = computeSessionRole({ user, units, unitSlug: unit.unit_slug });

  // FAZ-13 (1.4): uiOn'da saha kilidi kalkar — saf saha kullanıcı landing'le
  // zaten /saha'ya iner; buraya URL'le gelirse sayfa Gate'leri karar verir.
  if (!uiOn && sessionRole === "saha_personeli") {
    return <Navigate to={`${BASE}/${unit.unit_slug}/saha`} replace />;
  }

  return (
    <Layout user={user} unitName={unit.unit_name} unitSlug={unit.unit_slug}>
      <Outlet />
    </Layout>
  );
};

const NoSidebarLayoutRoute = () => {
  const { user, units, effectiveFallbackPath } = useKabuk();
  const { uiOn } = usePermissions();
  const { unitSlug } = useParams();

  const sessionRole = computeSessionRole({ user, units, unitSlug });
  // FAZ-13 (1.3): uiOn'da saha kabuğu role değil İZNE bağlı (aşağıdaki
  // saha route Gate'leri assignments.feedback.create ister) — karma izinli
  // kullanıcı (örn. başkan) saha sayfalarına girebilir.
  if (!uiOn && sessionRole !== "saha_personeli") {
    return <Navigate to={effectiveFallbackPath} replace />;
  }

  const unit = findUnitBySlug(units, unitSlug);
  if (!unit) return <Navigate to={`${BASE}/birim-sec`} replace />;

  localStorage.setItem(LS.activeUnitSlug, unit.unit_slug);
  localStorage.setItem(LS.activeUnitId, String(unit.unit_id));
  localStorage.setItem(
    LS.activeEffectiveRole,
    unit.effective_role || unit.role || user.role || ""
  );

  return (
    <BelediyeLayoutNoSidebar user={user} unitName={unit.unit_name} unitSlug={unit.unit_slug}>
      <Outlet />
    </BelediyeLayoutNoSidebar>
  );
};

// ── FAZ-22 — Canonical (birimsiz) kabuklar ─────────────────────────────────
// URL param yerine resolveActiveUnit (scope.mode + localStorage + units) kullanır.
// ADDITIVE: eski :unitSlug kabukları alias olarak KALIR. Backend birimi zaten
// X-Unit-Slug header'ından (localStorage) aldığı için birimsiz rota header'ı bozmaz.
const CanonicalSidebarShell = () => {
  const { user, units } = useKabuk();
  const authz = useSelector(selectAuthz);
  const active = resolveActiveUnit({
    scopeMode: authz.scope?.mode,
    activeUnitSlug: localStorage.getItem(LS.activeUnitSlug),
    units,
  });
  if (active.isAllUnits) {
    return (
      <Layout user={user} unitName="Başkanlık" unitSlug="baskanlik">
        <Outlet />
      </Layout>
    );
  }
  if (!active.unitSlug) {
    return <Navigate to={`${BASE}/birim-sec`} replace />;
  }
  const unit = findUnitBySlug(units, active.unitSlug);
  if (!unit) return <Navigate to={`${BASE}/birim-sec`} replace />;
  localStorage.setItem(LS.activeUnitSlug, unit.unit_slug);
  localStorage.setItem(LS.activeUnitId, String(unit.unit_id));
  localStorage.setItem(
    LS.activeEffectiveRole,
    unit.effective_role || unit.role || user.role || ""
  );
  return (
    <Layout user={user} unitName={unit.unit_name} unitSlug={unit.unit_slug}>
      <Outlet />
    </Layout>
  );
};

const CanonicalNoSidebarShell = () => {
  const { user, units } = useKabuk();
  const authz = useSelector(selectAuthz);
  const active = resolveActiveUnit({
    scopeMode: authz.scope?.mode,
    activeUnitSlug: localStorage.getItem(LS.activeUnitSlug),
    units,
  });
  const slug = active.isAllUnits ? "baskanlik" : active.unitSlug;
  const unit = findUnitBySlug(units, slug);
  // Saha kabuğu birim bağlamı ister; yoksa birim seçtir.
  if (!unit) return <Navigate to={`${BASE}/birim-sec`} replace />;
  localStorage.setItem(LS.activeUnitSlug, unit.unit_slug);
  localStorage.setItem(LS.activeUnitId, String(unit.unit_id));
  return (
    <BelediyeLayoutNoSidebar user={user} unitName={unit.unit_name} unitSlug={unit.unit_slug}>
      <Outlet />
    </BelediyeLayoutNoSidebar>
  );
};

//   Halk Masası layout (sidebar yok) — ROUTE HER ZAMAN REGISTER, GUARD İÇERDE
const HalkMasasiLayoutRoute = () => {
  const { user, effectiveFallbackPath } = useKabuk();
  const { uiOn, canAny } = usePermissions();
  const flagsLoaded = useSelector(selectIsFeatureFlagsLoaded);
  const flagsLoading = useSelector(selectIsFeatureFlagsLoading);
  const halkMasasiEnabled = useSelector(selectHalkMasasiEnabled);
  // FAZ-13 (1.1): uiOn'da HM kabuğuna giriş rol değil İZİN meselesi.
  if (uiOn) {
    // FAZ-13 (3): kabuk izni ⊇ alt-route izinleri — basvuru/:applicationId Gate'i
    // halk_masasi.timeline.view ister; yalnız o izne sahip kullanıcı da girebilmeli.
    if (!canAny(["halk_masasi.citizen.search", "halk_masasi.application.create", "halk_masasi.timeline.view"])) {
      return <Navigate to={`${BASE}/yetkisiz`} replace />;
    }
  } else if (user.role !== "personel_2") {
    return <Navigate to={effectiveFallbackPath} replace />;
  }

  // flags henüz gelmediyse: route var ama içerik loading (route yok probleminden kurtarır)
 if (!flagsLoaded || flagsLoading) {
return renderFullscreenLoading(
  "Modül Yükleniyor",
  "Halk Masası ayarları kontrol ediliyor..."
);
}

  // modül kapalıysa “yokmuş gibi”: güvenli fallback.
  // FAZ-13 (6): pür HM kullanıcının effectiveFallbackPath'i yine /halk-masasi'ya
  // çözülebilir (resolveLanding HM önceliği) → sonsuz redirect döngüsü. uiOn'da
  // HM-dışı güvenli hedefe düş; kapalı dal (legacy) BİREBİR korunur.
  if (!halkMasasiEnabled) {
    if (uiOn) {
      const safeTarget =
        effectiveFallbackPath &&
        !effectiveFallbackPath.startsWith(`${BASE}/halk-masasi`)
          ? effectiveFallbackPath
          : `${BASE}/yetkisiz`;
      return <Navigate to={safeTarget} replace />;
    }
    return <Navigate to={effectiveFallbackPath} replace />;
  }

  return (
<Layout user={user} unitName="Halk Masası" unitSlug="halk-masasi">
  <Outlet />
</Layout>
);
};

//   Sistem Yönetimi layout (FAZ-15) — HER ZAMAN REGISTER, GUARD İÇERDE
const SistemLayoutRoute = () => {
  const { user, effectiveFallbackPath } = useKabuk();
  const { uiOn, canAny } = usePermissions();
  if (uiOn) {
    // kabuk izni ⊇ TÜM alt-route izinleri (gerçek üst-küme). Aksi halde yalnız o
    // alt-route iznine sahip kullanıcı kabuktan /yetkisiz'e çarpar, sayfa
    // PermissionGate'i hiç çalışmaz. REVIEW #5: eski eksik kodlar (catalog.view,
    // units.*) + FAZ-24 (.view ×3) tam listelendi.
    if (!canAny([
      "users.view",
      "roles.manage", "permissions.catalog.view",
      "units.view", "units.manage", "units.manage_tree",
      "neighborhoods.view", "admin_boundaries.view", "citizens.view",
    ])) {
      return <Navigate to={`${BASE}/yetkisiz`} replace />;
    }
  } else {
    // permission_ui KAPALIYKEN Sistem Yönetimi workspace'i YOK → güvenli fallback
    return <Navigate to={effectiveFallbackPath} replace />;
  }
  return (
    <Layout user={user} unitName="Sistem Yönetimi" unitSlug="sistem">
      <Outlet />
    </Layout>
  );
};

export default function BelediyeApp() {
  const [user, setUser] = useState(null);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [globalLoadingState, setGlobalLoadingState] = useState({
    visible: false,
    title: "Yükleniyor",
    subtitle: "Lütfen bekleyiniz...",
  });
  const navigate = useNavigate();
  const location = useLocation();

  const dispatch = useDispatch();

  // FAZ-80: son uygulanan /me imzası. Aynı imza tekrar gelirse dispatch edilmez
  // (bkz. meImzasi yorumu) — böylece odak/görünürlük tazelemesi ekranı yeniden
  // çizmez, açık formlar ve gönderim bekleyen ekler yerinde kalır.
  const meImzaRef = useRef("");
  const authzUygula = useCallback(
    (me) => {
      const imza = meImzasi(me);
      if (imza === meImzaRef.current) return false;
      meImzaRef.current = imza;
      dispatch(authzSetFromMe(me));
      return true;
    },
    [dispatch]
  );

  //   FAZ-13: izin cutover'ı — uiOn=false iken AŞAĞIDAKİ TÜM eski rol yolları
  //   birebir çalışır; uiOn=true iken guard/landing izinden karar verir.
  const { uiOn } = usePermissions();
  const authz = useSelector(selectAuthz);

  //   flags state (FAZ-80: canAny/halkMasasiEnabled artık kabukların KENDİ
  //   okuması — kabuklar modül kapsamına taşındı, gövdede tüketicisi kalmadı)
  const flagsLoaded = useSelector(selectIsFeatureFlagsLoaded);
  const flagsLoading = useSelector(selectIsFeatureFlagsLoading);

  const currentUnitSlugFromPath = useMemo(() => {
    const parts = location.pathname.split("/").filter(Boolean);
    return parts[1] || "";
  }, [location.pathname]);

  // 1) Bootstrap: token + /me
  useEffect(() => {
    const initApp = async () => {
      setLoading(true);

      const queryParams = new URLSearchParams(location.search);
      const tokenFromQuery = queryParams.get("token");
      const userFromQueryRaw = queryParams.get("user");
      const userFromQuery = safeJsonParse(userFromQueryRaw);

      if (tokenFromQuery) localStorage.setItem(LS.accessToken, tokenFromQuery);
      if (userFromQuery) localStorage.setItem(LS.user, JSON.stringify(userFromQuery));

      if (tokenFromQuery || userFromQueryRaw) {
        navigate(location.pathname, { replace: true });
      }

      const storedToken = localStorage.getItem(LS.accessToken);
      if (!storedToken) {
        navigate("/giris", { replace: true });
        setLoading(false);
        return;
      }

      try {
        const meRes = await api.get("/common/users/me/");
        const me = meRes.data;

        let unitDetailFallback = null;
        const meUnits = Array.isArray(me?.units) ? me.units : [];

        if ((!meUnits || meUnits.length === 0) && typeof me?.unit === "number") {
          try {
            const unitRes = await api.get(`/common/units/${me.unit}/`);
            unitDetailFallback = unitRes.data;
          } catch (e) {
            //
          }
        }

        const normalizedUnits = normalizeUnitsFromMe(me, meUnits, unitDetailFallback);

        setUser(me);
        setUnits(normalizedUnits);
        localStorage.setItem(LS.user, JSON.stringify(me));

        // FAZ-12: aynı /me yanıtından authz slice'ı besle (ekstra istek yok)
        authzUygula(me);
      } catch (e) {
        localStorage.removeItem(LS.accessToken);
        localStorage.removeItem(LS.refreshToken);
        localStorage.removeItem(LS.user);
        localStorage.removeItem(LS.activeUnitSlug);
        localStorage.removeItem(LS.activeUnitId);
        localStorage.removeItem(LS.activeEffectiveRole);

        // FAZ-13 (5): bootstrap /me hatasında bayat izinler kalmasın (paylaşılan
        // makinede stale authz + interceptor'ın bayat uiOn okuması). authzCleared
        // reducer'ı authz_v1 storage'ını da temizler.
        dispatch(authzCleared());

        navigate("/giris", { replace: true });
      } finally {
        setLoading(false);
      }
    };

    initApp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 1b) FAZ-52 — MODÜL/ÖZELLİK BAYRAKLARINI TAZE TUT
  //
  // Sorun: modül ve özellik bayraklarının TEK kaynağı /me yanıtı ve /me yalnız
  // uygulama açılışında + birim değiştirince çekiliyordu. Admin bir modülü
  // (görev/mesajlaşma) ya da özelliği kapattığında kullanıcının ekranı sayfayı
  // YENİLEYENE KADAR eski hâlde kalıyordu — "yapı geç geliyor" şikâyeti buydu.
  //
  // Çözüm: sekme görünürken periyodik, ayrıca sekmeye geri dönüldüğünde anında
  // /me tazelenir ve YALNIZ authz slice'ı beslenir (user/units state'ine
  // dokunulmaz → ekran zıplamaz, açık form kaybolmaz).
  //
  // NOT: backend tarafında modül bayrakları 60 sn locmem cache'li ve cache
  // süreç-başına — çok işçili kurulumda değişikliğin görünmesi o kadar daha
  // gecikebilir. Buradaki periyot onunla aynı büyüklükte tutuldu.
  useEffect(() => {
    if (loading || !user) return undefined;

    const tazele = () => {
      if (document.hidden) return;
      const slug = (localStorage.getItem(LS.activeUnitSlug) || "").trim();
      api
        .get("/common/users/me/", slug ? { headers: { "X-Unit-Slug": slug } } : undefined)
        .then((res) => authzUygula(res.data))
        .catch(() => {}); // sessiz: bayrak tazeleme kullanıcıyı asla rahatsız etmez
    };

    // PERİYODİK YOKLAMA KALDIRILDI (kullanıcı kararı, 2026-07-27):
    // "sistem birkaç saniyede bir en baştan yenileniyor" şikâyeti üzerine
    // zamanlayıcı tamamen çıkarıldı. Tazeleme artık YALNIZ sekmeye dönünce /
    // pencere odaklanınca çalışır — kullanıcı ekranda çalışırken hiçbir şey
    // kendiliğinden yenilenmez.
    //
    // Bayrak/izin tazeliği bundan zarar görmez: asıl gecikme backend'deki
    // rol↔izin önbelleğinin iptal sinyali olmamasıydı ve o giderildi
    // (common_app/signals.py → invalidate_role_perm_cache).
    const onVis = () => !document.hidden && tazele();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
    };
  }, [loading, user, authzUygula]);

    useEffect(() => {
    const handleGlobalLoadingEvent = (event) => {
      const detail = event?.detail || {};

      setGlobalLoadingState((prev) => ({
        visible: detail.visible === true,
        title: detail.title || prev.title || "Yükleniyor",
        subtitle: detail.subtitle || prev.subtitle || "Lütfen bekleyiniz...",
      }));
    };

    window.addEventListener(
      BELEDIYE_GLOBAL_LOADING_EVENT,
      handleGlobalLoadingEvent
    );

    return () => {
      window.removeEventListener(
        BELEDIYE_GLOBAL_LOADING_EVENT,
        handleGlobalLoadingEvent
      );
    };
  }, []);


  // 2) user hazır olunca feature flags’i 1 kez yükle
  useEffect(() => {
    if (loading) return;
    if (!user) return;
    if (flagsLoaded || flagsLoading) return;

    dispatch(fetchFeatureFlags());
  }, [loading, user, flagsLoaded, flagsLoading, dispatch]);

  //   Halk masası kullanıcıları için unit cache temizliği (flag'den bağımsız)
  useEffect(() => {
    if (loading) return;
    if (!user) return;

    const sessionRole = getSessionRole({
      user,
      activeEffectiveRole: localStorage.getItem(LS.activeEffectiveRole),
      preferStorage: true,
    });

    if (isDeskStaff(sessionRole) || user.role === "personel_2") {
      localStorage.removeItem(LS.activeUnitSlug);
      localStorage.removeItem(LS.activeUnitId);
      localStorage.removeItem(LS.activeEffectiveRole);
    }
  }, [loading, user]);

  // 3) Kullanıcı yüklendikten sonra: birim seçimi kararını ver
  useEffect(() => {
    if (loading) return;
    if (!user) return;

    //   Halk Masası user — unit selection yok
    if (user.role === "personel_2") return;

    if (user.role === "başkan") {
      localStorage.setItem(LS.activeUnitSlug, "baskanlik");
      localStorage.setItem(LS.activeEffectiveRole, "başkan");
      return;
    }

    const list = Array.isArray(units) ? units : [];
    const activeSlug = localStorage.getItem(LS.activeUnitSlug);

    if (list.length === 1) {
      localStorage.setItem(LS.activeUnitSlug, list[0].unit_slug);
      localStorage.setItem(LS.activeUnitId, String(list[0].unit_id));
      localStorage.setItem(
        LS.activeEffectiveRole,
        list[0].effective_role || list[0].role || user.role || ""
      );
      return;
    }

    if (list.length >= 2 && !activeSlug) {
      if (location.pathname !== `${BASE}/birim-sec`) {
        navigate(`${BASE}/birim-sec`, { replace: true });
      }
    }
  }, [loading, user, units, navigate, location.pathname]);

  const activeSlug = useMemo(() => readActiveSlugOrDefault({ user, units }), [user, units]);

  const dashboardFallbackPath = useMemo(
    () => makeDashboardPath({ user, unitSlug: activeSlug }),
    [user, activeSlug]
  );

  //   FAZ-13 ADIM 2: landing = izin-öncelik listesi (uiOn'da tek fonksiyon).
  //   uiOn=false → eski makeDashboardPath aynen (effectiveFallbackPath onu döner).
  const izinliLandingPath = useMemo(() => {
    if (!uiOn || !user) return null;
    const activeSlugLS = localStorage.getItem(LS.activeUnitSlug);
    const list = Array.isArray(units) ? units : [];
    return resolveLanding(authz.permissions, {
      scopeMode: authz.scope?.mode,
      unitSlug:
        (activeSlugLS && activeSlugLS !== "baskanlik" ? activeSlugLS : "") ||
        (list.length === 1 ? list[0].unit_slug : ""),
      needsUnitSelection: list.length >= 2 && !activeSlugLS,
      isSuperUser: authz.isSuperUser, // superuser → Sistem Yönetimi (birim/çıkmaz yok)
      uiProfile: authz.uiProfile, // FAZ-20: halk_masasi/saha → kendi ekranına iner
    });
  }, [uiOn, user, authz, units]);

  const effectiveFallbackPath =
    uiOn && izinliLandingPath ? izinliLandingPath : dashboardFallbackPath;

  // FAZ-80: kabukların sayfa bağlamı. Değer DEĞİŞİNCE kabuklar yeniden ÇİZİLİR
  // ama SÖKÜLMEZ — kimlikleri modül kapsamında sabit (bkz. dosya başındaki not).
  const kabukDegeri = useMemo(
    () => ({ user, units, effectiveFallbackPath }),
    [user, units, effectiveFallbackPath]
  );
  if (loading) {
  return renderFullscreenLoading(
    "Belediye Paneli Hazırlanıyor",
    "Oturum ve kullanıcı bilgileri yükleniyor..."
  );
}
  // FAZ-63 — DERİN LİNK KORUMASI.
  // Önceden düz `/giris`e gidiliyordu ve kullanıcının GİTMEK İSTEDİĞİ adres
  // atılıyordu; SMS'ten gelen kişi giriş yaptıktan sonra görevini bulamıyor,
  // kendi varsayılan ekranına düşüyordu. Artık hedef `?next=` ile taşınıyor.
  // Yan kazanç: yalnız görev değil, TÜM derin linkler (yer imleri dâhil)
  // girişten sonra hedefine döner.
  if (!user) return <Navigate to={loginUrlWithNext(location.pathname, location.search)} replace />;

  const handleLogout = () => {
    localStorage.removeItem(LS.accessToken);
    localStorage.removeItem(LS.refreshToken);
    localStorage.removeItem(LS.user);
    localStorage.removeItem(LS.activeUnitSlug);
    localStorage.removeItem(LS.activeUnitId);
    localStorage.removeItem(LS.activeEffectiveRole);
    // FAZ-13 (5): çıkışta izin durumunu da temizle (authz_v1 storage dahil).
    dispatch(authzCleared());
    navigate("/giris", { replace: true });
  };

  const handleSelectUnit = (unit) => {
    if (!unit?.unit_slug) return;

    localStorage.setItem(LS.activeUnitSlug, unit.unit_slug);
    localStorage.setItem(LS.activeUnitId, String(unit.unit_id || ""));
    localStorage.setItem(
      LS.activeEffectiveRole,
      unit.effective_role || unit.role || user.role || ""
    );

    const role = unit.effective_role || unit.role || user.role || "";

    // FAZ-13 (3.9): uiOn'da seçim-sonrası hedef, SEÇİLEN birimin izin setiyle
    // resolveLanding'den (izinler birime göre değişir → /me beklenir).
    if (uiOn) {
      api
        .get("/common/users/me/", { headers: { "X-Unit-Slug": unit.unit_slug } })
        .then((res) => {
          authzUygula(res.data);
          navigate(
            resolveLanding(res.data?.permissions, {
              scopeMode: res.data?.scope?.mode,
              unitSlug: unit.unit_slug,
              needsUnitSelection: false,
              isSuperUser: res.data?.is_superuser,
              uiProfile: res.data?.ui_profile, // FAZ-20
            }),
            { replace: true }
          );
        })
        .catch(() => {
          // /me alınamazsa güvenli düşüş — FAZ-22 4b: canonical (birim localStorage'da)
          navigate(`${BASE}/dashboard`, { replace: true });
        });
      return;
    }

    // FAZ-12: izinler birime göre değişir — /me yeni birim bağlamıyla tazelenir
    // (fire-and-forget; şalter kapalıyken sonucu kimse okumaz, akış değişmez)
    api
      .get("/common/users/me/", { headers: { "X-Unit-Slug": unit.unit_slug } })
      .then((res) => authzUygula(res.data))
      .catch(() => {});

    if (role === "saha_personeli") {
      navigate(`${BASE}/${unit.unit_slug}/saha`, { replace: true });
    } else {
      navigate(`${BASE}/${unit.unit_slug}/dashboard`, { replace: true });
    }
  };


  const isMultiUnit = Array.isArray(units) && units.length >= 2;

  return (
    <>
      {/* E2: sağ-alt "+" görev FAB'ı KALDIRILDI — görev atama artık sohbet
          üzerinden adım adım (Faz-C TaskComposer). QuickCreateForm görev
          sayfalarında kullanılmaya devam eder. */}
      {/* Mesajlaşma: her rotada sağ-alt sohbet penceresi + unread-poll sahibi
          (yalnız uiOn + mesajlasma modülü + mesajlasma.view — kendi kapısını taşır). */}
      <ChatDock />
      {/* FAZ-90 — ALT NAVİGASYON BARI.
          Telefon değilse AltBar null döner; burada koşul yazılmaz.
          Çıkış performLogout'a bağlanır — BelediyeLogoutButton'ın ve navbar
          kebap menüsünün kullandığı AYNI işlev; iki farklı çıkış yolu olmaz. */}
      <AltBar cikisYap={performLogout} />
      <FloatingBackButton
        fallbackTo={effectiveFallbackPath}
        hideOnPaths={[
          `${BASE}/birim-sec`,
          `${BASE}/baskanlik/dashboard`,
          `${BASE}/halk-masasi`,
          `${BASE}/halk-masasi/sorgu`,
          currentUnitSlugFromPath ? `${BASE}/${currentUnitSlugFromPath}/dashboard` : "",
        ].filter(Boolean)}
      />
      {globalLoadingState.visible && (
        <UnifiedLoading
          visible={true}
          variant="fullscreen"
          municipalityName={municipalityName}
          logoSrc={kimLogo}
          title={globalLoadingState.title}
          subtitle={globalLoadingState.subtitle}
        />
      )}
      {/* FAZ-12: yalnız authz-403 toast'ı için (uiOn kapalıyken hiç toast atılmaz) */}
      <ToastContainer position="top-center" autoClose={4000} />
      {/* FAZ-84 — telefon bildirimi: manifest değişimi + nöbetçi eşitlemesi +
          bildirime dokunulduğunda yönlendirme. Hiçbir şey çizmez; giriş
          yapılmış alanın içindedir (yukarıdaki `if (!user)` kapısından sonra). */}
      <PushBaslatici />
      <KabukContext.Provider value={kabukDegeri}>
      <Routes>
        {/* FAZ-12: yetkisiz sayfası — YENİ path, mevcut akışlara dokunmaz
            (PermissionGate FAZ-13'te buraya yönlendirecek) */}
        <Route path="yetkisiz" element={<YetkisizPage />} />

        {/* /yonetim — FAZ-13 (ADIM 2): uiOn'da landing tek fonksiyondan */}
        <Route
          index
          element={
            uiOn ? (
              <Navigate to={effectiveFallbackPath} replace />
            ) : user.role === "personel_2" ? (
              <Navigate to={`${BASE}/halk-masasi`} replace />
            ) : isMultiUnit && !localStorage.getItem(LS.activeUnitSlug) ? (
              <Navigate to={`${BASE}/birim-sec`} replace />
            ) : (
              <Navigate to={effectiveFallbackPath} replace />
            )
          }
        />

        {/*   Halk Masası routes — HER ZAMAN REGISTER */}
        <Route path="halk-masasi" element={<HalkMasasiLayoutRoute />}>
  <Route index element={<Navigate to="sorgu" replace />} />
  <Route path="sorgu" element={<PermissionGate permissions={["halk_masasi.citizen.search"]}><HalkMasasiLookupPage /></PermissionGate>} />
  <Route path="yeni-basvuru" element={<PermissionGate permissions={["halk_masasi.application.create"]}><HalkMasasiCreateApplicationPage /></PermissionGate>} />
  <Route path="vatandas/:citizenId" element={<PermissionGate permissions={["halk_masasi.citizen.search"]}><HalkMasasiCitizenProfilePage /></PermissionGate>} />
  <Route path="vatandas/:citizenId/yeni-basvuru" element={<PermissionGate permissions={["halk_masasi.application.create"]}><HalkMasasiCreateApplicationPage /></PermissionGate>} />
  <Route path="basvuru/:applicationId" element={<PermissionGate permissions={["halk_masasi.timeline.view"]}><HalkMasasiApplicationDetailPage /></PermissionGate>} />
  {/*   Profil route da burada olmalı (Navbar fix) */}
  <Route path="profil" element={<ProfilePage />} />
</Route>

        {/*   Sistem Yönetimi routes (FAZ-15) — HER ZAMAN REGISTER */}
        <Route path="sistem" element={<SistemLayoutRoute />}>
          <Route index element={<Navigate to="kullanicilar" replace />} />
          <Route path="kullanicilar" element={<PermissionGate permissions={["users.view"]}><SistemKullanicilarPage /></PermissionGate>} />
          <Route path="kullanicilar/:userId" element={<PermissionGate permissions={["users.view"]}><SistemKullaniciDetayPage /></PermissionGate>} />
          <Route path="roller" element={<PermissionGate permissions={["roles.manage", "permissions.catalog.view"]}><SistemRollerPage /></PermissionGate>} />
          <Route path="roller/:roleId" element={<PermissionGate permissions={["roles.manage", "permissions.catalog.view"]}><SistemRollerPage /></PermissionGate>} />
          <Route path="birim-agaci" element={<PermissionGate permissions={["units.view", "units.manage", "units.manage_tree"]}><SistemBirimAgaciPage /></PermissionGate>} />
          {/* FAZ-24 — yeni yönetim ekranları */}
          <Route path="mahalleler" element={<PermissionGate permissions={["neighborhoods.view"]}><SistemMahallelerPage /></PermissionGate>} />
          <Route path="idari-sinirlar" element={<PermissionGate permissions={["admin_boundaries.view"]}><SistemIdariSinirlarPage /></PermissionGate>} />
          <Route path="vatandaslar" element={<PermissionGate permissions={["citizens.view"]}><SistemVatandaslarPage /></PermissionGate>} />
          <Route path="vatandaslar/:citizenId" element={<PermissionGate permissions={["citizens.view"]}><SistemVatandasDetayPage /></PermissionGate>} />
          <Route path="profil" element={<ProfilePage />} />
        </Route>

        {/* Birim seç */}
        <Route
          path="birim-sec"
          element={
            user.role === "personel_2" ? (
              <Navigate to={`${BASE}/halk-masasi`} replace />
            ) : (
              <UnitSelectionPage
                units={(Array.isArray(units) ? units : []).filter((u) => u.is_active !== false)}
                onSelect={handleSelectUnit}
                onLogout={handleLogout}
              />
            )
          }
        />

        {/* FAZ-22 — CANONICAL (birimsiz) rotalar. ADDITIVE: static literaller
            (dashboard/basvurular/personel/raporlar/bildirimler/saha) gerçek birim
            slug'larından ÖNCE eşleşir; eski /yonetim/:unitSlug/... alias KALIR.
            Birim resolveActiveUnit'ten (localStorage + scope.mode) çözülür. */}
        <Route element={<CanonicalSidebarShell />}>
          <Route path="dashboard" element={<PermissionGate permissions={["dashboard.view"]}><DashboardV2Page /></PermissionGate>} />
          <Route path="basvurular" element={<PermissionGate permissions={["applications.view"]}><NewApplicationsPage /></PermissionGate>} />
          <Route path="basvurular/:id" element={<PermissionGate permissions={["applications.view"]}><ApplicationDetailsPage /></PermissionGate>} />
          <Route path="raporlar" element={<PermissionGate permissions={["reports.view"]}><ReportsPage /></PermissionGate>} />
          <Route path="personel" element={<PermissionGate permissions={["personel.directory.view"]}><PersonelDizinPage /></PermissionGate>} />
          <Route path="personel/:userId" element={<PermissionGate permissions={["personel.directory.view"]}><PersonelProfilPage /></PermissionGate>} />
          <Route path="bildirimler" element={<NotificationsPage />} />
          {/* FAZ-84 — telefon bildiriminden gelen tek giriş kapısı. Nöbetçi
              (public/sw.js) yalnız bu adresi bilir; hedefi burada
              resolveNotificationTarget çözer. İzin kapısı YOK: kişi yalnız
              KENDİ bildirimini okuyabilir (uç recipient=request.user süzer). */}
          <Route path="b/:bildirimId" element={<BildirimKopruPage />} />
          {/* FAZ-22 ADIM-4b tamamlama: yonlendirilen/anketler/profil canonical (birimSİZ).
              Eski /yonetim/:unitSlug/... alias route'ları AŞAĞIDA duruyor (silinmedi). */}
          <Route path="yonlendirilen-basvurular" element={<PermissionGate permissions={["staff.forwardlog.report.view"]}><RedirectedApplicationsPage /></PermissionGate>} />
          <Route path="anketler" element={<PermissionGate permissions={["surveys.report.view"]}><SurveyReportsPage /></PermissionGate>} />
          <Route path="anketler/:surveyId" element={<PermissionGate permissions={["surveys.report.view"]}><SurveyReportDetailPage /></PermissionGate>} />
          <Route path="profil" element={<ProfilePage />} />
          {/* FAZ-34: Duyuru Gönder — bildirim.duyuru.gonder izni */}
          <Route path="duyuru-olustur" element={<PermissionGate permissions={["bildirim.duyuru.gonder"]}><DuyuruOlusturPage /></PermissionGate>} />

          {/* FAZ-28 — Kurum İçi Görevler (R2 §3 rotaları). Görünürlük KİMLİKTEN:
              bu uçlara X-Unit-Slug gitmez; kişiye atanmış görev her çalışma
              alanında görünür. Detay AYRI SAYFA (drawer değil — R2 kararı). */}
          {/* FAZ-56 — sayfa AYRIMI: kapsam sekmeden değil SAYFADAN gelir.
              Görev Ata = verenin sayfası · Görevlerim = yapanın sayfası.
              Detay rotası DEĞİŞMEDİ (bildirim/sohbet/başvuru köprüleri buraya
              gidiyor). Eski /gorevler yer imi izin duyarlı yönlendirilir. */}
          <Route path="gorevler" element={<GorevlerEskiYerImi />} />
          <Route path="gorevler/:taskId" element={<ModuleGate module="internal_tasks"><PermissionGate permissions={["tasks.view"]}><GorevDetayPage /></PermissionGate></ModuleGate>} />
          {/* tasks.create de kapıda: başvuru/sohbet köprüleri bu adrese görev
              OLUŞTURMAK için gelir; liste görme yetkisi olmayan kullanıcıda
              sayfa listesiz açılır (sayfa kendi içinde kapsamı düşürür). */}
          {/* FAZ-91 — canonical "Talepler" rotası. Alt bardaki Talepler
              yuvası buraya gider; sayfa tarafa göre iki listeden birini
              çizer. Eski rotalar ALIAS olarak duruyor (FAZ-22 deseni):
              yer imleri, bildirim köprüleri ve sohbet bağlantıları kırılmaz. */}
          <Route path="talepler" element={<ModuleGate module="internal_tasks"><PermissionGate permissions={["tasks.view_created", "tasks.view_assigned", "tasks.view_unit_pool", "tasks.create"]}><TaleplerPage /></PermissionGate></ModuleGate>} />
          <Route path="gorev-ata" element={<ModuleGate module="internal_tasks"><PermissionGate permissions={["tasks.view_created", "tasks.view_all", "tasks.create"]}><GorevAtaPage /></PermissionGate></ModuleGate>} />
          <Route path="gorevlerim" element={<ModuleGate module="internal_tasks"><PermissionGate permissions={["tasks.view_assigned", "tasks.view_unit_pool"]}><GorevlerimPage /></PermissionGate></ModuleGate>} />
          {/* "gorev-raporu" rotası 2026-07-28'de KALDIRILDI (kullanıcı kararı:
              "böyle bir istatistiğe gerek yok"). Sayfa, uçları ve izin kodları
              da söküldü — bkz. FAZ-62. */}

          {/* FAZ-94 — KURUM İÇİ SOHBET, GERÇEK ROTA.
              Telefonda mesajlaşma bir katman değil sayfa: liste · yeni · detay.
              Tablet ve masaüstünde bu rotalara normalde GELİNMEZ (orada yüzen
              pencere liste+sohbeti yan yana gösteriyor, K-1); yine de derin
              link ya da yer imiyle açılabilsin diye kapı katmana değil İZNE
              bağlı. Görünürlük KİMLİKTEN (katılımcılık) → X-Unit-Slug gitmez.
              Sıra önemli: "yeni" statik parçası ":conversationId"den ÖNCE
              tanımlanmalı, yoksa /sohbet/yeni bir sohbet kimliği sanılır. */}
          <Route path="sohbet" element={<ModuleGate module="mesajlasma"><PermissionGate permissions={["mesajlasma.view"]}><SohbetlerPage /></PermissionGate></ModuleGate>} />
          <Route path="sohbet/yeni" element={<ModuleGate module="mesajlasma"><PermissionGate permissions={["mesajlasma.view"]}><YeniSohbetPage /></PermissionGate></ModuleGate>} />
          <Route path="sohbet/:conversationId" element={<ModuleGate module="mesajlasma"><PermissionGate permissions={["mesajlasma.view"]}><SohbetDetayPage /></PermissionGate></ModuleGate>} />
        </Route>
        {/* FAZ-22 — Canonical saha: /yonetim/saha/gorevlerim */}
        <Route path="saha" element={<CanonicalNoSidebarShell />}>
          {/* FAZ-16: canonical saha birimSİZ → unitSlug prop GEÇİLMİYORDU, sayfa
              "Birim bilgisi yükleniyor…" ekranında takılıyordu. activeSlug
              (localStorage + tek-birim default) geçir → saha girişte açılır. */}
          <Route path="gorevlerim" element={<PermissionGate permissions={["assignments.feedback.create"]}><SahaPersoneliDashboardPage unitSlug={activeSlug} /></PermissionGate>} />
          <Route path="gorevlerim/:participantId" element={<PermissionGate permissions={["assignments.feedback.create"]}><SahaPersoneliAssignmentDetailPage unitSlug={activeSlug} /></PermissionGate>} />
          <Route path="profil" element={<ProfilePage variant="saha" />} />
        </Route>

        {/* Saha (no-sidebar): /yonetim/:unitSlug/saha */}
        <Route path=":unitSlug/saha" element={<NoSidebarLayoutRoute />}>
          <Route index element={<PermissionGate permissions={["assignments.feedback.create"]}><SahaPersoneliDashboardPage /></PermissionGate>} />
          <Route path=":participantId" element={<PermissionGate permissions={["assignments.feedback.create"]}><SahaPersoneliAssignmentDetailPage /></PermissionGate>} />
          <Route path="profil" element={<ProfilePage variant="saha" />} />
        </Route>

        {/* Sidebar’lı: /yonetim/:unitSlug/... */}
        <Route path=":unitSlug" element={<WithSidebarLayoutRoute />}>
          
          <Route path="profil" element={<ProfilePage />} />

          <Route path="bildirimler" element={<NotificationsPage />} />
          {/* FAZ-34: Duyuru Gönder — bildirim.duyuru.gonder izni */}
          <Route path="duyuru-olustur" element={<PermissionGate permissions={["bildirim.duyuru.gonder"]}><DuyuruOlusturPage /></PermissionGate>} />

          <Route path="dashboard" element={<PermissionGate permissions={["dashboard.view"]}><DashboardV2Page /></PermissionGate>} />

          {/* Tek sekmeli "Başvurular" ekranı (statü sekmeleri sayfa içinde) */}
          <Route path="basvurular" element={<PermissionGate permissions={["applications.view"]}><NewApplicationsPage /></PermissionGate>} />

          {/* Eski statü route'ları — geriye-uyum (sidebar'da yok, deep-link çalışır) */}
          <Route path="yeni-basvurular" element={<PermissionGate permissions={["applications.view"]}><NewApplicationsPage /></PermissionGate>} />
          <Route path="incelemedeki-basvurular" element={<PermissionGate permissions={["applications.view"]}><PendingApplicationsPage /></PermissionGate>} />
          <Route path="cozulen-basvurular" element={<PermissionGate permissions={["applications.view"]}><ResolvedApplicationsPage /></PermissionGate>} />
          <Route path="cozulemeyen-basvurular" element={<PermissionGate permissions={["applications.view"]}><UnresolvedApplicationsPage /></PermissionGate>} />
          <Route path="yonlendirilen-basvurular" element={<PermissionGate permissions={["staff.forwardlog.report.view"]}><RedirectedApplicationsPage /></PermissionGate>} />

          <Route path="raporlar" element={<PermissionGate permissions={["reports.view"]}><ReportsPage /></PermissionGate>} />

          {/* FAZ-18: Personel modülü (belediye workspace; modül-gate backend'de) */}
          <Route path="personel" element={<PermissionGate permissions={["personel.directory.view"]}><PersonelDizinPage /></PermissionGate>} />
          <Route path="personel/:userId" element={<PermissionGate permissions={["personel.directory.view"]}><PersonelProfilPage /></PermissionGate>} />

          {/* FAZ-13 (1.2): uiOn'da anketler route'u HERKESE register + Gate —
              non-yetkili dogrudan URL artik catch-all :id'ye dusup basvuru
              sanilmiyor, /yetkisiz goruyor (etiketli davranis duzeltmesi). */}
          {(user.role === "başkan" || uiOn) && (
            <>
              <Route path="anketler" element={<PermissionGate permissions={["surveys.report.view"]}><SurveyReportsPage /></PermissionGate>} />
              <Route path="anketler/:surveyId" element={<PermissionGate permissions={["surveys.report.view"]}><SurveyReportDetailPage /></PermissionGate>} />
              <Route path="bildirimler" element={<NotificationsPage />} />
            </>
          )}

          <Route path=":id" element={<PermissionGate permissions={["applications.view"]}><ApplicationDetailsPage /></PermissionGate>} />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to={effectiveFallbackPath} replace />} />
      </Routes>
      </KabukContext.Provider>
    </>
  );
}